import { ThingEventSnapshot } from '@eagle/core-data-types';
import { EventSummarizationTemplate, ProcessedEvent, ProcessedEventType, SummarizationMode } from './event-summarization-types';
import { groupEvents, matchEvent, selectRepresentative } from './event-summarization-utility';

export const summarizeEvents = (
  notableEventsOnly: ThingEventSnapshot[],
  template: EventSummarizationTemplate,
): ProcessedEvent[] => {
  try {
    const results: ProcessedEvent[] = [];
    const processedEventIds = new Set<string>();

    if (!template || typeof template !== 'object') {
      return notableEventsOnly.map((event) => ({ type: ProcessedEventType.Individual, event }));
    }

    // Process each feature in template
    for (const [feature, featureConfig] of Object.entries(template)) {
      const { eventTypeIds, matchRules } = featureConfig;

      for (const [eventTypeId, summarizationMode] of Object.entries(eventTypeIds)) {
        const workingSet = notableEventsOnly.filter((event) => event.feature === feature && event.eventTypeId === eventTypeId && !processedEventIds.has(event.eventId));
        if (workingSet.length === 0) {
          continue;
        }

        // Process each match rule
        for (const rule of matchRules) {
          if (rule.minCount < 1) {
            continue;
          }
          const { match, keyCombination, minCount } = rule;

          try {
            const matched = workingSet.filter((event) => {
              const matches = matchEvent(event, match);
              return matches;
            });

            if (matched.length === 0) {
              continue;
            }

            // Group events
            const { grouped, individual: individualFromGrouping } = groupEvents(matched, keyCombination);

            // Add individual events from missing keys first
            individualFromGrouping.forEach((event) => {
              if (!processedEventIds.has(event.eventId)) {
                results.push({ type: ProcessedEventType.Individual, event });
                processedEventIds.add(event.eventId);
              }
            });

            // Process each group
            for (const [groupKey, groupEventsList] of grouped.entries()) {
              // Mark all events in this group as processed
              groupEventsList.forEach((event) => processedEventIds.add(event.eventId));
              if (groupEventsList.length < minCount || summarizationMode === SummarizationMode.All) {
                groupEventsList.forEach((event) => {
                  results.push({ type: ProcessedEventType.Individual, event });
                });
              } else {
                const representative = selectRepresentative(groupEventsList, summarizationMode);
                if (!Array.isArray(representative)) {
                  results.push({
                    type: ProcessedEventType.Summarized,
                    representativeEvent: representative,
                    eventCount: groupEventsList.length,
                    originalEvents: groupEventsList,
                    feature,
                    eventTypeId,
                    groupKey,
                    summarizationMode,
                  });
                }
              }
            }
          } catch (error) {
            console.error(`Error processing rule for ${feature}.${eventTypeId}:`, error);
            continue;
          }
        }
      }
    }
    // Add any remaining notable events
    const remainingEvents = notableEventsOnly.filter((event) => !processedEventIds.has(event.eventId));
    remainingEvents.forEach((event) => { results.push({ type: ProcessedEventType.Individual, event }); });

    return results;
  } catch (error) {
    console.error('Error in summarizeEvents, falling back to individual events:', error);
    // Complete fallback - return all events as individual
    return notableEventsOnly.map((event) => ({ type: ProcessedEventType.Individual, event }));
  }
};
